// F4 Report 16: Project Plan vs Actual
import React, { useEffect, useState, useCallback } from "react";
import { fetchReportPlanVsActual } from "../../api/service";
import ReportShell, { downloadReportCsv } from "./ReportShell";

const STAGE_ORDER = [
  { key: "cutlist", label: "Cutlist" },
  { key: "factory_production", label: "Factory Production" },
  { key: "factory_dispatch", label: "Factory Dispatch" },
  { key: "carcass", label: "Carcass" },
  { key: "accessories", label: "Accessories" },
  { key: "final_fitting", label: "Final Fitting" },
];

const CENTRES = [
  { key: "", display: "All Centres" },
  { key: "bangalore", display: "Bangalore" },
  { key: "hyderabad", display: "Hyderabad" },
  { key: "mumbai", display: "Mumbai" },
  { key: "delhi", display: "Delhi" },
];

// Delta cell: green if <= 0 (on plan / ahead), red if > 0 (slipped)
const deltaColor = (d) => {
  if (d === null || d === undefined) return "";
  if (d <= 0) return "text-success fw-bold";
  return "text-danger fw-bold";
};

// Build tooltip text from a stage object (planned + actual dates)
const stageTooltip = (s) => {
  if (!s) return "";
  const parts = [];
  if (s.planned_start) parts.push(`Planned start: ${s.planned_start}`);
  if (s.planned_end) parts.push(`Planned end: ${s.planned_end}`);
  if (s.actual_start) parts.push(`Actual start: ${s.actual_start}`);
  if (s.actual_end) parts.push(`Actual end: ${s.actual_end}`);
  return parts.join(" | ");
};

// Format cell content: empty state, "Not started", or number
const cellVal = (v, notStarted) => {
  if (notStarted) return "Not started";
  if (v === null || v === undefined) return "-";
  return v;
};

const PlanVsActualReport = () => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [centre, setCentre] = useState("");
  const [projectQuery, setProjectQuery] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    fetchReportPlanVsActual(centre, projectQuery)
      .then((res) => {
        setRows(res?.rows || []);
        setCount(res?.count || 0);
      })
      .catch((e) => setError(e?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [centre, projectQuery]);

  useEffect(() => {
    const t = setTimeout(load, 300); // debounce
    return () => clearTimeout(t);
  }, [load]);

  const buildQueryString = () => {
    const params = [];
    if (centre) params.push(`centre=${encodeURIComponent(centre)}`);
    if (projectQuery) params.push(`project=${encodeURIComponent(projectQuery)}`);
    return params.length ? `?${params.join("&")}` : "";
  };

  return (
    <ReportShell
      title="16. Project Plan vs Actual"
      count={count}
      loading={loading}
      error={error}
      onExport={() => downloadReportCsv("plan_vs_actual", buildQueryString())}
    >
      {/* Filter row */}
      <div className="d-flex align-items-center mb-3 flex-wrap" style={{ gap: "1rem" }}>
        <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
          <label className="mb-0" style={{ fontWeight: 500 }}>Execution Centre:</label>
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: "200px" }}
            value={centre}
            onChange={(e) => setCentre(e.target.value)}
          >
            {CENTRES.map((c) => (
              <option key={c.key} value={c.key}>{c.display}</option>
            ))}
          </select>
        </div>
        <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
          <label className="mb-0" style={{ fontWeight: 500 }}>Project:</label>
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ maxWidth: "250px" }}
            placeholder="ID or customer name"
            value={projectQuery}
            onChange={(e) => setProjectQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-hover table-sm align-middle" style={{ fontSize: "0.85rem" }}>
          <thead className="table-light">
            <tr>
              <th rowSpan={2} className="text-center align-middle">Project</th>
              {STAGE_ORDER.map((s) => (
                <th key={s.key} colSpan={3} className="text-center">{s.label}</th>
              ))}
              <th colSpan={3} className="text-center">Total Project</th>
              <th rowSpan={2} className="text-center align-middle">Days Pending</th>
            </tr>
            <tr>
              {STAGE_ORDER.map((s) => (
                <React.Fragment key={s.key + "-sub"}>
                  <th className="text-center">P</th>
                  <th className="text-center">A</th>
                  <th className="text-center">Δ</th>
                </React.Fragment>
              ))}
              <th className="text-center">P</th>
              <th className="text-center">A</th>
              <th className="text-center">Δ</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={2 + STAGE_ORDER.length * 3 + 4} className="text-center text-muted py-4">
                  No projects match the filters.
                </td>
              </tr>
            ) : rows.map((r) => (
              <tr key={r.project_id}>
                <td>{r.project_label}</td>
                {STAGE_ORDER.map((s) => {
                  const stage = r.stages?.[s.key] || {};
                  const notStarted = stage.state === "not_started";
                  return (
                    <React.Fragment key={s.key + "-cells"}>
                      <td className="text-center" title={stageTooltip(stage)}>
                        {cellVal(stage.planned_days, false)}
                      </td>
                      <td className="text-center" title={stageTooltip(stage)}>
                        {cellVal(stage.actual_days, notStarted)}
                      </td>
                      <td className={`text-center ${deltaColor(stage.delta_days)}`} title={stageTooltip(stage)}>
                        {notStarted ? "-" : cellVal(stage.delta_days, false)}
                      </td>
                    </React.Fragment>
                  );
                })}
                <td className="text-center">{cellVal(r.total?.planned_days, false)}</td>
                <td className="text-center">{cellVal(r.total?.actual_days, false)}</td>
                <td className={`text-center ${deltaColor(r.total?.delta_days)}`}>
                  {cellVal(r.total?.delta_days, false)}
                </td>
                <td
                    className={`text-center ${r.project_completed ? "text-success fw-bold" : deltaColor(-1 * (r.days_pending || 0))}`}
                    title={r.ipcd_end ? `IPCD end: ${r.ipcd_end}` : ""}
                >
                  {r.project_completed
                    ? "Completed"
                    : (r.days_pending === null || r.days_pending === undefined ? "-" : r.days_pending)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ReportShell>
  );
};

export default PlanVsActualReport;
